<script setup lang="ts">
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import WorkspaceSwitcher from '@/components/WorkspaceSwitcher.vue'

defineProps<{
  title?: string
}>()

const auth = useAuthStore()
const route = useRoute()
const router = useRouter()

const navItems = [
  { name: 'home', label: 'Dashboard', icon: 'dashboard' },
  { name: 'contacts', label: 'Relationships', icon: 'group' },
  { name: 'linkedin-accounts', label: 'LinkedIn accounts', icon: 'hub' },
  { name: 'campaigns', label: 'Campaigns', icon: 'campaign' },
  { name: 'inbox', label: 'Inbox', icon: 'inbox' },
  { name: 'integrations', label: 'Enrichment', icon: 'bolt' },
  { name: 'signals', label: 'Signals', icon: 'sensors' },
]

const secondaryLinks = [
  { label: 'Settings', icon: 'settings' },
  { label: 'Support', icon: 'help_outline' },
]

async function handleLogout() {
  await auth.logout()
  await router.push({ name: 'login' })
}
</script>

<template>
  <div class="min-h-screen bg-surface">
    <aside
      class="fixed left-0 top-0 z-50 flex h-screen w-sidebar-width flex-col border-r border-outline-variant/40 bg-surface-container-low py-8"
    >
      <div class="mb-10 px-6">
        <h1 class="font-serif text-2xl font-semibold text-on-surface">
          SidedoorHQ
        </h1>
      </div>

      <nav class="flex-1 space-y-1 px-4">
        <RouterLink
          v-for="item in navItems"
          :key="item.name"
          :to="{ name: item.name }"
          class="flex items-center gap-3 rounded-md px-3 py-2 text-ui-label transition-colors cursor-pointer"
          :class="
            route.matched.some((m) => m.name === item.name)
              ? 'bg-surface-container-highest font-semibold text-text-primary'
              : 'text-text-secondary hover:bg-surface-container-high hover:text-text-primary'
          "
        >
          <span
            class="material-symbols-outlined text-[20px]"
            aria-hidden="true"
          >{{ item.icon }}</span>
          <span>{{ item.label }}</span>
        </RouterLink>

        <RouterLink
          v-if="auth.isSuperadmin"
          :to="{ name: 'admin-ai-usage' }"
          class="flex items-center gap-3 rounded-md px-3 py-2 text-ui-label transition-colors cursor-pointer"
          :class="
            route.matched.some((m) => m.name === 'admin-ai-usage' || m.name === 'admin-ai-usage-show')
              ? 'bg-surface-container-highest font-semibold text-text-primary'
              : 'text-text-secondary hover:bg-surface-container-high hover:text-text-primary'
          "
        >
          <span
            class="material-symbols-outlined text-[20px]"
            aria-hidden="true"
          >monitoring</span>
          <span>AI usage</span>
        </RouterLink>
      </nav>

      <div class="px-4">
        <div class="my-3 border-t border-outline-variant/40" />
        <div class="space-y-1">
          <button
            v-for="link in secondaryLinks"
            :key="link.label"
            type="button"
            disabled
            title="Coming soon"
            class="flex w-full cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-left text-ui-label text-text-secondary/50"
          >
            <span
              class="material-symbols-outlined text-[20px]"
              aria-hidden="true"
            >{{ link.icon }}</span>
            <span>{{ link.label }}</span>
            <span class="ml-auto text-metadata text-text-secondary/50">Soon</span>
          </button>
        </div>
      </div>

      <div class="mt-auto px-4">
        <div class="mb-3">
          <WorkspaceSwitcher />
        </div>
        <div class="flex items-center justify-between gap-2 rounded-lg border border-outline-variant/40 bg-surface-container-lowest p-3">
          <div class="min-w-0">
            <p class="truncate text-ui-label font-semibold text-on-surface">
              {{ auth.user?.name }}
            </p>
            <p class="truncate text-metadata text-on-surface-variant">
              {{ auth.user?.email }}
            </p>
          </div>
          <button
            type="button"
            aria-label="Sign out"
            class="shrink-0 text-on-surface-variant hover:text-on-surface"
            @click="handleLogout"
          >
            <span class="material-symbols-outlined">logout</span>
          </button>
        </div>
      </div>
    </aside>

    <main class="ml-sidebar-width min-h-screen">
      <header
        class="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-outline-variant/40 bg-surface px-gutter"
      >
        <div class="flex items-center gap-4">
          <slot name="title">
            <h2 class="text-headline-md text-on-surface">
              {{ title }}
            </h2>
          </slot>
        </div>
        <div class="flex items-center gap-4">
          <slot name="actions" />
        </div>
      </header>

      <div class="p-gutter">
        <slot />
      </div>
    </main>
  </div>
</template>
